<template>
  <v-app id="inspire" class="ism-auth-app" light>
    <div class="ism-auth-bg" :style="backgroundStyle"></div>

    <v-main class="ism-auth-main">
      <v-container fluid fill-height class="pa-0">
        <v-row align="center" justify="center" no-gutters style="height: 100vh;">
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" class="d-flex justify-center">
            <div class="ism-auth-card">
              <div class="ism-auth-header">
                <img src="/LogoSvg.svg" alt="iSmartSociety" class="ism-auth-logo" />

                <template v-if="step === 'email'">
                  <h2 class="ism-auth-title">Forgot Password</h2>
                  <p class="ism-auth-subtitle">Enter your admin email to receive a reset OTP</p>
                </template>
                <template v-else>
                  <h2 class="ism-auth-title">Verify OTP</h2>
                  <p class="ism-auth-subtitle">Enter the OTP sent to your email and choose a new password</p>
                </template>
              </div>

              <template v-if="step === 'email'">
                <v-form @submit.prevent="sendEmail" class="ism-auth-form">
                  <PremiumInput
                    v-model="user.email"
                    name="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    type="email"
                    icon="mdi-email-outline"
                    v-validate="'required|email'"
                    :error="errors.first('email') || serverSideErrors.email"
                    @input="$set(serverSideErrors, 'email', '')"
                  />

                  <button type="submit" class="ism-auth-btn" :disabled="loading">
                    <v-progress-circular v-if="loading" indeterminate size="20" color="#fff" class="mr-2"></v-progress-circular>
                    <span v-else>Send OTP</span>
                  </button>
                </v-form>

                <div class="ism-auth-hint mt-4">
                  <v-icon small color="#F26522">mdi-information-outline</v-icon>
                  <span>Use the email registered with your super admin account</span>
                </div>
              </template>

              <template v-else>
                <div class="otp-mobile-badge">
                  <v-icon small color="#F26522">mdi-email-outline</v-icon>
                  <span class="otp-mobile-num">{{ user.email }}</span>
                  <button type="button" class="otp-change-btn" @click="backToEmail">
                    Change
                  </button>
                </div>

                <v-form @submit.prevent="resetPassword" class="ism-auth-form">
                  <div class="otp-container mt-4 mb-4">
                    <div class="otp-inputs">
                      <input
                        v-for="(digit, index) in 6"
                        :key="index"
                        ref="otpBoxes"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        maxlength="1"
                        class="otp-box"
                        :value="otpArray[index]"
                        autocomplete="one-time-code"
                        @input="onOtpInput(index, $event)"
                        @keydown="onOtpKeydown(index, $event)"
                        @paste="onOtpPaste"
                      />
                    </div>
                    <div v-if="serverSideErrors.otp || serverSideErrors.token" class="error-msg mt-2">
                      {{ serverSideErrors.otp || serverSideErrors.token }}
                    </div>
                  </div>

                  <PremiumInput
                    v-model="resetUser.password"
                    name="password"
                    label="New Password"
                    placeholder="Enter new password"
                    :type="showPassword ? 'text' : 'password'"
                    icon="mdi-lock-outline"
                    v-validate="'required'"
                    :error="errors.first('password') || serverSideErrors.password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showPassword = !showPassword"
                    @input="$set(serverSideErrors, 'password', '')"
                  />

                  <PremiumInput
                    v-model="resetUser.password_confirmation"
                    name="password_confirmation"
                    data-vv-as="Confirm Password"
                    label="Confirm Password"
                    placeholder="Re-enter new password"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    icon="mdi-lock-check-outline"
                    v-validate="'required'"
                    :error="errors.first('password_confirmation') || serverSideErrors.password_confirmation"
                    :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    @input="$set(serverSideErrors, 'password_confirmation', '')"
                  />

                  <button type="submit" class="ism-auth-btn" :disabled="loading">
                    <v-progress-circular v-if="loading" indeterminate size="20" color="#fff" class="mr-2"></v-progress-circular>
                    <span v-else>Reset Password</span>
                  </button>
                </v-form>

                <div class="ism-auth-hint mt-4">
                  <v-icon small color="#F26522">mdi-information-outline</v-icon>
                  <span>Check your email inbox for the 6-digit OTP</span>
                </div>
              </template>

              <div class="mt-4 text-center">
                <router-link to="/superadmin/login" class="ism-auth-link">
                  Back to Login
                </router-link>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <Alert />
  </v-app>
</template>

<script>
import Alert from "../../Alert.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import "@/assets/style.css";
import { mapMutations } from "vuex";

export default {
  components: {
    Alert,
    PremiumInput,
  },
  data() {
    return {
      step: "email",
      showPassword: false,
      showConfirmPassword: false,
      user: {
        email: "",
      },
      resetUser: {
        password: "",
        password_confirmation: "",
      },
      otpArray: ["", "", "", "", "", ""],
      loading: false,
      serverSideErrors: {},
    };
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: `url(${process.env.VUE_APP_API_URL}/web_front/bulding.svg)`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },
    themeColor() {
      return "#F26522";
    },
    otp() {
      return this.otpArray.join("");
    },
  },
  created() {
    document.documentElement.style.setProperty("--brand", this.themeColor);
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    onOtpInput(index, event) {
      const val = event.target.value;
      if (val && !/^\d$/.test(val)) {
        this.$set(this.otpArray, index, "");
        event.target.value = "";
        return;
      }
      this.$set(this.otpArray, index, val);
      this.$set(this.serverSideErrors, "otp", "");
      this.$set(this.serverSideErrors, "token", "");
      if (val && index < 5) {
        this.$refs.otpBoxes[index + 1].focus();
      }
    },

    onOtpKeydown(index, event) {
      if (event.key === "Backspace" && !this.otpArray[index] && index > 0) {
        this.$refs.otpBoxes[index - 1].focus();
      }
    },

    onOtpPaste(event) {
      event.preventDefault();
      const pasteData = (event.clipboardData || window.clipboardData).getData("text");
      const digits = pasteData.replace(/\D/g, "").split("").slice(0, 6);
      digits.forEach((digit, index) => {
        this.$set(this.otpArray, index, digit);
      });
      if (digits.length > 0) {
        this.$refs.otpBoxes[Math.min(digits.length, 5)].focus();
      }
    },

    backToEmail() {
      this.step = "email";
      this.serverSideErrors = {};
      this.$validator.reset();
    },

    sendEmail() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;

        this.loading = true;
        this.serverSideErrors = {};

        this.$axios
          .post("/superadmin/forgotPassword", this.user)
          .then((response) => {
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.step = "reset";
              this.$nextTick(() => {
                if (this.$refs.otpBoxes && this.$refs.otpBoxes[0]) {
                  this.$refs.otpBoxes[0].focus();
                }
              });
            } else if (data.errors) {
              data.errors.forEach((error) => {
                this.$set(this.serverSideErrors, error.field, error.message);
              });
            } else {
              this.setAlert({
                showAlert: true,
                variant: "red accent-3",
                timeout: 2000,
                message: data.message,
              });
            }
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },

    resetPassword() {
      if (this.otp.length !== 6) {
        this.$set(this.serverSideErrors, "otp", "OTP must be exactly 6 digits.");
        return;
      }

      this.$validator.validateAll().then((result) => {
        if (!result) return;

        this.loading = true;
        this.serverSideErrors = {};

        this.$axios
          .post("/superadmin/resetPassword/" + this.otp, this.resetUser)
          .then((response) => {
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                variant: "green accent-4",
                timeout: 2000,
                message: data.message,
              });
              this.$router.push({ path: "/superadmin/login" });
            } else if (data.errors) {
              data.errors.forEach((error) => {
                this.$set(this.serverSideErrors, error.field, error.message);
              });
            } else {
              this.$set(this.serverSideErrors, "otp", data.message || "Invalid OTP");
              this.setAlert({
                showAlert: true,
                variant: "red accent-3",
                timeout: 2000,
                message: data.message,
              });
            }
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
  },
};
</script>

<style scoped>
.ism-auth-app {
  background-color: #f8fafc !important;
}

.ism-auth-card {
  --card-bg: #ffffff !important;
  --bg-primary: #f1f5f9 !important;
  --text-primary: #1e293b !important;
  --text-secondary: #64748b !important;
  --border-color: rgba(0, 0, 0, 0.09) !important;
  --border-color2: rgba(0, 0, 0, 0.15) !important;
}

.ism-auth-card ::v-deep .premium-label {
  color: #475569 !important;
  text-align: left;
}

.ism-auth-card ::v-deep .premium-input .v-input__slot {
  background: #f1f5f9 !important;
}

.ism-auth-card ::v-deep .premium-input fieldset {
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.ism-auth-card ::v-deep .premium-input input {
  color: #1e293b !important;
}

.ism-auth-card ::v-deep .premium-input input::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}

.ism-auth-card ::v-deep .premium-input .v-icon,
.ism-auth-card ::v-deep .premium-input .v-label {
  color: #64748b !important;
}

.ism-auth-card ::v-deep input:-webkit-autofill,
.ism-auth-card ::v-deep input:-webkit-autofill:hover,
.ism-auth-card ::v-deep input:-webkit-autofill:focus,
.ism-auth-card ::v-deep input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #f1f5f9 inset !important;
  -webkit-text-fill-color: #1e293b !important;
}

.ism-auth-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.ism-auth-main {
  position: relative;
  z-index: 2;
}

.ism-auth-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 48px 40px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.ism-auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.ism-auth-logo {
  height: 48px;
  max-width: 100%;
  margin-bottom: 24px;
  object-fit: contain;
}

.ism-auth-title {
  font-family: "Inter", sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px;
  letter-spacing: 0;
}

.ism-auth-subtitle {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.ism-auth-form {
  display: flex;
  flex-direction: column;
}

.ism-auth-link,
.otp-change-btn {
  border: 0;
  background: transparent;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #F26522;
  text-decoration: none;
  cursor: pointer;
}

.ism-auth-link:hover,
.otp-change-btn:hover {
  color: #C94F10;
}

.ism-auth-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #F26522, #C94F10);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(242, 101, 34, 0.3);
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 8px;
}

.ism-auth-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(242, 101, 34, 0.4);
}

.ism-auth-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.ism-auth-hint {
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 11px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #64748b;
  text-align: left;
}

.otp-mobile-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  background: var(--surface2, #f9fafb);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  margin-bottom: 4px;
}

.otp-mobile-num {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  color: var(--tx, #111827);
}

.otp-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.otp-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.otp-box {
  width: 44px;
  height: 44px;
  border: 2px solid var(--border, #e5e7eb);
  border-radius: 12px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  color: var(--tx, #111827);
  background: var(--surface, #ffffff);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.otp-box:focus {
  outline: none;
  border-color: var(--brand, #F26522);
  box-shadow: 0 0 0 4px rgba(242, 101, 34, 0.15);
  transform: translateY(-2px);
}

.error-msg {
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
}

@media (max-width: 600px) {
  .ism-auth-card {
    padding: 40px 24px;
    border-radius: 20px;
    margin: 16px;
  }

  .otp-inputs {
    gap: 8px;
  }

  .otp-box {
    width: 38px;
    height: 42px;
    font-size: 21px;
  }
}
</style>
